import React, { useState, useEffect } from 'react'
import { withStyles } from '@mui/styles'
import { Box, TextField, Typography } from '@mui/material'

import { filterBasicInputStyles } from './filter-basic-input.styles'
import { useTypingRefresh } from '../../core/utils/useTypingRefresh'
import { useNavigate, useLocation } from 'react-router-dom'

const DEFAULT_TYPE_REFRESH_MS = 800

/**
 * @typedef {object} FilterBasicInputProps
 * @property {object} classes - Objeto con los nombres de las clases CSS inyectado por `withStyles`.
 * @property {number} [typeRefresh=800] - El tiempo de espera en milisegundos (debounce) antes de actualizar la URL después de que el usuario deja de escribir.
 * @property {string} name - El nombre del parámetro de búsqueda en la URL.
 * @property {string} label - El texto de la etiqueta que se muestra dentro del campo de entrada.
 */

/**
 * Un componente de campo de texto especializado para filtros que sincroniza automáticamente
 * su valor con los parámetros de búsqueda de la URL.
 * 
 * Implementa un mecanismo de debounce (retardo) para evitar actualizaciones excesivas de la URL
 * mientras el usuario escribe. Cuando el valor cambia, actualiza el parámetro correspondiente
 * en la URL y resetea la paginación a la página 1.
 * 
 * **Nota:** Este componente depende de `react-router-dom` para la navegación y gestión de URL.
 * 
 * @component
 * @example
 * const MyFilterPanel = () => {
 *   // El valor se sincroniza automáticamente con la URL
 *   // Por ejemplo, si la URL es "?productName=laptop&_pagina=2"
 *   // el campo mostrará "laptop" y al cambiar actualizará la URL
 * 
 *   return (
 *     <FilterBasicInput
 *       name="productName"
 *       label="Nombre del Producto"
 *       typeRefresh={1000} // Espera 1 segundo después de que el usuario deja de escribir
 *     />
 *   );
 * };
 * 
 * @param {FilterBasicInputProps} props - Las props del componente FilterBasicInput.
 */
export const FilterBasicInput = withStyles(filterBasicInputStyles)(({
  classes,
  typeRefresh = DEFAULT_TYPE_REFRESH_MS,
  ...props
}) => {
  const [inputValue, setInputValue] = useState('')
  const navigate = useNavigate()
  const location = useLocation()
  const currentSearch = new URLSearchParams(location.search)
  const searchQuery = currentSearch.get(props.name)

  useEffect(() => setInputValue(searchQuery ? searchQuery : ''), [searchQuery])

  const changeSearchValue = (search) => {
    if ((!search || search.length === 0) && !currentSearch.get(props.name)) {
      // Do nothing
    } else {
      currentSearch.set(props.name, search)
      currentSearch.set('_pagina', 1)
      navigate({
        search: '?' + currentSearch.toString(),
      })
    }
  }

  const { ref: textRef } = useTypingRefresh({
    timeout: typeRefresh,
    action: (val) => changeSearchValue(val),
    textFromRef: (ref) => (ref.current ? ref.current.querySelector('input') : null),
  })
  return (
    <Box style={{ flex: 1 }} ref={textRef}>
      <TextField
        className={classes.root}
        variant="outlined"
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        InputProps={{
          className: classes.input,
          startAdornment: (
            <Typography variant="body1" className={classes.title}>
              {props.label}:
            </Typography>
          ),
        }}
      />
    </Box>
  )
})
